import React, { FC, Fragment, useEffect, useState } from "react";
import styles from "./details-editing.module.scss";
import CustomScrollbar from "../../../ui-kit/scrollbar/scrollbar";
import DetailsForm from "../../../../shared/details-form/details-form";
import Button from "../../../ui-kit/button/button";
import { createPortal } from "react-dom";
import DocumentKrookie from "../../../../shared/document-krookie/document-krookie";
import {
  CardKrookies,
  DocumentOrigin,
  FormDataEditDetails,
  KrookieStatus,
} from "../../../../interfaces/krookie";
import {
  clearAllSelected,
  KrookieFormationTypeId,
  setBlockNavigation,
  setFormDataEditDetails,
  setResetKrookieData,
} from "../../../../services/reducers/master-plan-modification";
import ModalSuccessfulCreateKrookie from "../modals/modal-successful-create-krookie/modal-successful-create-krookie";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../../hooks/useAppSelector";
import { useAppDispatch } from "../../../../hooks/useAppDispatch";
import { setEditingBorders } from "../../../../services/actions/master-plan-modification";
import { takeScreenshotAsync } from "../../../../services/actions/map";
import { getScreenshotScalesForExtent } from "../../../../utils/krookie-helpers";
import Geometry from "@arcgis/core/geometry/Geometry";

interface DetailsEditingProps {
  requiredFields: (keyof FormDataEditDetails)[];
  cardKrookieData: Record<number, CardKrookies>;
  krookieFormationTypeId: KrookieFormationTypeId;
  disabledFields?: Partial<Record<keyof FormDataEditDetails, boolean>>;
  status?: KrookieStatus;
  onToggleCloseMasterPlanModal?: () => void;
}

const DetailsEditing: FC<DetailsEditingProps> = ({
  requiredFields,
  cardKrookieData,
  krookieFormationTypeId,
  status = KrookieStatus.Project,
  disabledFields = {},
  onToggleCloseMasterPlanModal,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const modalPortal = document.getElementById("react-modals");

  const { formDataEditDetails, krookiePlots, selectedPlots } = useAppSelector(
    (state) => state.masterPlanModification,
  );

  const [visibleDocumentKrookie, setVisibleDocumentKrookie] =
    useState<boolean>(false);
  const [visibleModalSuccessful, setVisibleModalSuccessful] =
    useState<boolean>(false);
  const [isRefreshingScreenshots, setIsRefreshingScreenshots] =
    useState<boolean>(false);

  const objectIds = Object.keys(formDataEditDetails).map(Number);

  const isValid = objectIds.every((objectId) => {
    const formData = formDataEditDetails[objectId];
    if (!formData) return false;

    return requiredFields.every((field) => {
      if (
        field === "phase" ||
        field === "landUse" ||
        field === "cityEn" ||
        field === "status" ||
        field === "locationStatusCode"
      ) {
        return (
          formData[field].value !== null && formData[field].displayValue !== ""
        );
      }
      return formData[field] !== "";
    });
  });

  useEffect(() => {
    dispatch(setBlockNavigation(true));

    return () => {
      dispatch(setBlockNavigation(false));
    };
  }, []);

  const handleFormDataChange = (
    objectId: number,
    formData: FormDataEditDetails,
  ) => {
    dispatch(
      setFormDataEditDetails({
        id: objectId,
        formData,
      }),
    );
  };

  const handleSeeKrookie = async () => {
    // Re-capture the document screenshots with the plot number just
    // finalized on this form — the geometry step captured them earlier
    // using whatever plot number existed at that point, which can be
    // stale/blank once the user sets the real number here.
    // The real plot(s) this operation is replacing (set when the wizard
    // started) — for Merge/Divide these still exist on the server with
    // their old geometry until approval, so they need excluding too, not
    // just each new plot's own id.
    const excludeObjectIds = selectedPlots.map((plot) => plot.objectId);

    const screenshotParamsArray = krookiePlots
      .filter((plot) => objectIds.includes(plot.objectId))
      .flatMap((plot) => {
        const geometry = plot.newGeometry as Geometry;
        return getScreenshotScalesForExtent(
          geometry?.extent?.width,
          geometry?.extent?.height,
        ).map((fixedScale, index) => ({
          fixedScale,
          geometry,
          objectId: plot.objectId,
          excludeObjectIds,
          label: formDataEditDetails[plot.objectId]?.plotNumber ?? plot.number,
          // Only the pair's closer/tighter scale (the second, smaller
          // value) gets the numbered corner labels.
          showCornerLabels: index === 1,
        }));
      });

    if (screenshotParamsArray.length > 0) {
      setIsRefreshingScreenshots(true);
      try {
        await dispatch(takeScreenshotAsync(screenshotParamsArray));
      } catch (err) {
        console.error("Failed to refresh krookie screenshots:", err);
      } finally {
        setIsRefreshingScreenshots(false);
      }
    }

    setVisibleDocumentKrookie(true);
  };

  const handleModalSuccessfulClose = () => {
    dispatch(setEditingBorders("reset"));
    dispatch(clearAllSelected());
    dispatch(setResetKrookieData());
    setVisibleModalSuccessful(false);
    navigate("/masterPlan");
  };

  const handleClickViewJournal = () => {
    dispatch(setEditingBorders("reset"));
    dispatch(clearAllSelected());
    dispatch(setResetKrookieData());
    setVisibleModalSuccessful(false);
    navigate("/repository/krookie");
  };

  const modalPlotNumber = objectIds
    .map((objectId) => {
      const formData = formDataEditDetails[objectId];
      return formData.plotNumber;
    })
    .join(", ");
  const hasMultiplePlots = objectIds.length > 1;

  return (
    <div className={styles.editDetails}>
      <div className={styles.content}>
        <CustomScrollbar>
          <div className={styles.formsContainer}>
            {objectIds.map((objectId, index) => {
              const formData = formDataEditDetails[objectId];
              if (!formData) return null;

              return (
                <Fragment key={objectId}>
                  <h4 className={styles.title}>
                    {hasMultiplePlots
                      ? t("editDetailsBlock.plotInformationNumbered", {
                          number: index + 1,
                        })
                      : t("editDetailsBlock.plotInformation")}
                  </h4>
                  <DetailsForm
                    key={objectId}
                    formData={formData}
                    onFormDataChange={(data) =>
                      handleFormDataChange(objectId, data)
                    }
                    disabled={disabledFields}
                  />
                </Fragment>
              );
            })}
          </div>
        </CustomScrollbar>
        <div className={styles.buttonSection}>
          <Button
            type="primary"
            className={`${styles.button} ${styles.next}`}
            onClick={handleSeeKrookie}
            disabled={!isValid || isRefreshingScreenshots}
          >
            {t("editDetailsBlock.seeKrookie")}
          </Button>
          <Button
            onClick={() => navigate(-1)}
            type="outline"
            className={`${styles.button} ${styles.cancel}`}
          >
            {t("editDetailsBlock.return")}
          </Button>
        </div>
      </div>
      {visibleDocumentKrookie &&
        modalPortal &&
        createPortal(
          <DocumentKrookie
            setVisibleModalSuccessful={setVisibleModalSuccessful}
            setVisibleDocumentKrookie={setVisibleDocumentKrookie}
            data={cardKrookieData}
            status={status}
            origin={DocumentOrigin.Edit}
            krookieFormationTypeId={krookieFormationTypeId}
            onToggleCloseMasterPlanModal={onToggleCloseMasterPlanModal}
          />,
          modalPortal,
        )}
      {visibleModalSuccessful &&
        modalPortal &&
        createPortal(
          <ModalSuccessfulCreateKrookie
            onClose={handleModalSuccessfulClose}
            viewJournal={handleClickViewJournal}
            plotNumber={modalPlotNumber}
          />,
          modalPortal,
        )}
    </div>
  );
};

export default DetailsEditing;
